import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactModal({ isOpen, onClose, type = 'contact' }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const titles = {
    contact: 'Contact Us',
    demo: 'Schedule a Demo',
    audit: 'Free Strategy Audit'
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const endpoint = type === 'demo' ? '/demo-request' : type === 'audit' ? '/strategy-audit' : '/contact';
      
      await axios.post(`${API}${endpoint}`, {
        ...formData,
        request_type: type
      });

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      
      // Close after success
      setTimeout(() => {
        onClose();
        setStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || t('forms.error'));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-80"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="contact-modal"
          className="w-full max-w-md glass-strong rounded-2xl p-6 md:p-8 border border-[var(--border-highlight)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold gradient-text-gold">
              {titles[type]}
            </h2>
            <button
              onClick={onClose}
              data-testid="modal-close-btn"
              className="p-2 rounded-lg hover:bg-[var(--bg-subtle)] transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>

          {/* Success State */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <p className="text-lg font-medium text-[var(--text-primary)]">
                {t('forms.success')}
              </p>
            </motion.div>
          )}

          {/* Form */}
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[var(--text-secondary)]">{t('forms.name')} *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="form-name"
                  className="bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--primary-main)]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[var(--text-secondary)]">{t('forms.email')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="form-email"
                  className="bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--primary-main)]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[var(--text-secondary)]">{t('forms.phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="form-phone"
                    className="bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--primary-main)]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-[var(--text-secondary)]">{t('forms.company')}</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="form-company"
                    className="bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--primary-main)]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[var(--text-secondary)]">{t('forms.message')} *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="form-message"
                  className="bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-primary)] focus:border-[var(--primary-main)] resize-none"
                />
              </div>

              {/* Error Message */}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === 'loading'}
                data-testid="form-submit-btn"
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('forms.submitting')}
                  </>
                ) : (
                  t('forms.submit')
                )}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
